import { isError, isString } from 'lodash';
import { FC, useCallback, type PropsWithChildren } from 'react';
import { SWRConfig, SWRConfiguration } from 'swr';
import { logger } from '@/utils/logger';

interface Props {}
export const GlobalSWRConfigProvider: FC<PropsWithChildren<Props>> = ({ children }) => {
  /**
   * Global SWR Error Handler.
   * Primarily reports to Sentry and Datadog.
   */
  const onSWRError = useCallback<NonNullable<SWRConfiguration['onError']>>((error, key, config) => {
    const errorMessage = isError(error) ? error.message : isString(error) ? error : JSON.stringify(error);
    logger.error(errorMessage, error, {
      description: 'Global SWR Error Handler',
      swrKey: key,
      swrConfig: {
        isOnline: config.isOnline(),
        isPaused: config.isPaused(),
        isVisible: config.isVisible(),
        revalidateOnMount: config.revalidateOnMount,
        revalidateIfStale: config.revalidateIfStale,
        revalidateOnFocus: config.revalidateOnFocus,
        revalidateOnReconnect: config.revalidateOnReconnect,
      },
    });
  }, []);

  return (
    <SWRConfig
      value={{
        /**
         * Only affects in "mounting" phase.
         * Used to ""force"" override the SWR revalidation behavior on mounting
         * Useful if we want to make lazy hooks by trigger fetch manually with `mutate()`.
         *
         * @caution Setting this option to `false` cause swr to never requests even there is no data in cache.
         * @default undefined - aka "auto" = requests if no cached data
         */
        revalidateOnMount: undefined, // ! DO NOT CHANGE ME !

        /**
         * Only affects in "mounting" phase.
         * Whether to fetch/revalidate if there is exiting cached data.
         * @default true (if not set)
         */
        revalidateIfStale: true,

        /**
         * Whether to re-validate if a page regains focus.
         * i.e., users switch tab/window and come back to the page.
         */
        revalidateOnFocus: false,

        /**
         * Whether to re-validate if a page come back online. (using `navigator.onLine`)
         */
        revalidateOnReconnect: false,

        /**
         * Global SWR Error Reporter.
         * @see Global_Error_Report - https://swr.vercel.app/docs/error-handling#global-error-report
         */
        onError: onSWRError,
      }}
    >
      {children}
    </SWRConfig>
  );
};
